import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { addDoc, collection, doc, getDocs, updateDoc } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { schoolClassSortOrder } from '../services/TimetableService';
import './AdminEventsView.css';
import './StudentDetailPages.css';
import StatCards from './StatCards';
import { scopeForAdmin, schoolMatches } from '../utils/schoolScope';

function SuperAdminEventsView({ adminData }) {
  const [saSchoolId, setSaSchoolId] = useState('');
  const [saSchools, setSaSchools] = useState([]);
  useEffect(() => {
    getDocs(collection(db, 'schools')).then(snap => setSaSchools(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
  }, []);
  // Dropdown is authoritative: "All Schools" ('' ) = national view
  adminData = useMemo(() => ({ ...(adminData || {}), schoolId: saSchoolId || null }), [adminData, saSchoolId]);
  const [events, setEvents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(true);

  const [newEvent, setNewEvent] = useState({
    name: '',
    eventType: 'Marathon',
    date: '',
    location: { latitude: 0, longitude: 0, address: '' },
    description: '',
    participatingClassIds: [],
    imageURL: ''
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const eventTypes = ['Marathon', 'Walkathon', 'Fun Run', 'Track and Field', 'Field Trip', 'Assembly', 'Sporting Event', 'Walk', 'Other'];

  const loadEvents = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'events'));
      let eventsData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      eventsData = scopeForAdmin(eventsData, adminData);
      setEvents(eventsData);
    } catch (error) {
      console.error('🔥 AdminEvents: Error loading events:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  const loadClasses = useCallback(async () => {
    try {
      const snapshot = await getDocs(collection(db, 'classes'));
      let classesData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      classesData = scopeForAdmin(classesData, adminData);
      classesData.sort(schoolClassSortOrder);
      setClasses(classesData);
    } catch (error) {
      console.error('🔥 AdminEvents: Error loading classes:', error);
    }
  }, [adminData]);

  useEffect(() => {
    console.log('🔥 AdminEvents: Component mounted, starting data load');
    loadEvents();
    loadClasses();
  }, [loadEvents, loadClasses]);

  const handleAddEvent = async (e) => {
    e.preventDefault();
    try {
      let imageURL = newEvent.imageURL;
      
      if (selectedImage) {
        setIsUploadingImage(true);
        const imageRef = ref(storage, `event_images/${Date.now()}_${selectedImage.name}`);
        await uploadBytes(imageRef, selectedImage);
        imageURL = await getDownloadURL(imageRef);
        setIsUploadingImage(false);
      }

      await addDoc(collection(db, 'events'), {
        ...newEvent,
        imageURL,
        date: new Date(newEvent.date).toISOString(),
        isActive: true,
        status: 'upcoming',
        createdAt: new Date().toISOString(),
        schoolId: adminData?.schoolId || null
      });
      setShowAddForm(false);
      setNewEvent({
        name: '',
        eventType: 'Marathon',
        date: '',
        location: { latitude: 0, longitude: 0, address: '' },
        description: '',
        participatingClassIds: [],
        imageURL: ''
      });
      setSelectedImage(null);
      setImagePreview(null);
      loadEvents();
    } catch (error) {
      console.error('Error adding event:', error);
      alert('Failed to add event');
      setIsUploadingImage(false);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return;
    try {
      await updateDoc(doc(db, 'events', eventId), { isActive: false });
      loadEvents();
    } catch (error) {
      console.error('Error deleting event:', error);
      alert('Failed to delete event');
    }
  };

  const handleClassToggle = (classId) => {
    setNewEvent(prev => {
      const selected = prev.participatingClassIds.includes(classId);
      if (selected) {
        return { ...prev, participatingClassIds: prev.participatingClassIds.filter(id => id !== classId) };
      } else {
        return { ...prev, participatingClassIds: [...prev.participatingClassIds, classId] };
      }
    });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(null);
  };

  const getEventIcon = (eventType) => {
    const icons = {
      'Marathon': '🏃',
      'Walkathon': '🚶',
      'Fun Run': '🏃',
      'Track and Field': '🏟️',
      'Field Trip': '🚌',
      'Assembly': '👥',
      'Sporting Event': '⚽',
      'Walk': '🚶',
      'Other': '📅'
    };
    return icons[eventType] || '📅';
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return 'N/A';
    if (dateValue.toDate) return dateValue.toDate().toLocaleDateString();
    return new Date(dateValue).toLocaleDateString();
  };

  const schoolNameFor = (sid) => saSchools.find(s => schoolMatches(s.id, sid))?.name || '';

  const classNameFor = (cid) => classes.find(c => c.id === cid)?.name || cid;

  if (selectedEvent) {
    const ev = selectedEvent;
    const loc = typeof ev.location === 'object' ? ev.location?.address : ev.location;
    console.log('🔥 SuperAdminEvents: Opened event detail', ev.id);
    return (
      <div className="detail-page">
        <button className="detail-back-btn" onClick={() => setSelectedEvent(null)}>◄ Back</button>
        {ev.imageURL && <img src={ev.imageURL} alt={ev.name} className="detail-hero-img" />}
        <div className="detail-hero">
          <div className="detail-hero-icon">{getEventIcon(ev.eventType)}</div>
          <div>
            <h2 className="detail-page-title">
              {ev.name || 'Event'}
              <span className={`detail-badge ${(ev.status || '').toLowerCase()}`}>{ev.status || 'upcoming'}</span>
            </h2>
            <p className="detail-page-subtitle">{ev.eventType || 'Event'} · {formatDate(ev.date)}</p>
          </div>
        </div>

        <div className="detail-card">
          <h4>Event Details</h4>
          <div className="detail-rows">
            <div className="detail-row"><span className="detail-label">School</span><span className="detail-value">{schoolNameFor(ev.schoolId) || ev.schoolId || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Type</span><span className="detail-value">{ev.eventType || 'N/A'}</span></div>
            <div className="detail-row"><span className="detail-label">Date</span><span className="detail-value">{formatDate(ev.date)}</span></div>
            <div className="detail-row"><span className="detail-label">Location</span><span className="detail-value">{loc || 'N/A'}</span></div>
            {ev.createdAt && (
              <div className="detail-row"><span className="detail-label">Created</span><span className="detail-value">{formatDate(ev.createdAt)}</span></div>
            )}
          </div>
        </div>

        {ev.description && (
          <div className="detail-card">
            <h4>Description</h4>
            <p className="detail-note">{ev.description}</p>
          </div>
        )}

        {ev.participatingClassIds && ev.participatingClassIds.length > 0 && (
          <div className="detail-card">
            <h4>Participating Classes</h4>
            <div className="detail-item-list">
              {ev.participatingClassIds.map(cid => (
                <div key={cid} className="detail-item-row"><span>{classNameFor(cid)}</span></div>
              ))}
            </div>
          </div>
        )}

        <div className="detail-actions">
          <button className="btn-delete" onClick={() => { handleDeleteEvent(ev.id); setSelectedEvent(null); }}>Delete Event</button>
        </div>
      </div>
    );
  }

  if (loading) {
    console.log('🔥 AdminEvents: Rendering loading state');
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  console.log('🔥 AdminEvents: Rendering main view, events:', events.length, 'classes:', classes.length);

  return (
    <div className="admin-events-view">
      <div className="view-header">
        <h2>Events</h2>
        <div className="sa-school-filter"><label>School:</label><select value={saSchoolId} onChange={(e) => setSaSchoolId(e.target.value)}><option value="">All Schools</option>{saSchools.map(s => <option key={s.id} value={s.id}>{s.name || s.id}</option>)}</select></div>
        <button className="add-button" onClick={() => setShowAddForm(true)}>
          + Add Event
        </button>
      </div>
      <StatCards items={[['Events',events.length],['Upcoming',events.filter(e => new Date(e.date) > new Date()).length],['Past',events.filter(e => new Date(e.date) < new Date() && new Date(e.date).toDateString() !== new Date().toDateString()).length],['Today',events.filter(e => new Date(e.date).toDateString() === new Date().toDateString()).length],['Classes',classes.length]]} />

      <p className="count-text">Events: {events.length}</p>

      {showAddForm && (
        <div className="add-event-form">
          <h3>Create New Event</h3>
          <form onSubmit={handleAddEvent}>
            <div className="form-group">
              <label>Event Name *</label>
              <input
                type="text"
                value={newEvent.name}
                onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Event Type *</label>
                <select
                  value={newEvent.eventType}
                  onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
                  required
                >
                  {eventTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Date *</label>
                <input
                  type="date"
                  value={newEvent.date}
                  onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                  required
                />
              </div>
            </div>
            <div className="form-group">
              <label>Location Address</label>
              <input
                type="text"
                value={newEvent.location?.address || ''}
                onChange={(e) => setNewEvent({ ...newEvent, location: { ...newEvent.location, address: e.target.value } })}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                rows={3}
              />
            </div>
            <div className="form-group">
              <label>Event Image</label>
              <div className="image-upload-container">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  id="event-image-input"
                  style={{ display: 'none' }}
                />
                <label htmlFor="event-image-input" className="image-upload-button">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="image-preview" />
                  ) : (
                    <div className="image-placeholder">
                      <span>📷</span>
                      <span>Tap to add event image</span>
                    </div>
                  )}
                </label>
                {selectedImage && (
                  <button
                    type="button"
                    className="remove-image-button"
                    onClick={handleRemoveImage}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Participating Classes</label>
              <div className="class-checkboxes">
                {classes.map(cls => (
                  <label key={cls.id} className="class-checkbox">
                    <input
                      type="checkbox"
                      checked={newEvent.participatingClassIds.includes(cls.id)}
                      onChange={() => handleClassToggle(cls.id)}
                    />
                    <span>{cls.name} ({cls.grade})</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="form-actions">
              <button type="button" onClick={() => setShowAddForm(false)}>
                Cancel
              </button>
              <button type="submit" disabled={isUploadingImage}>
                {isUploadingImage ? 'Uploading Image...' : 'Create Event'}
              </button>
            </div>
          </form>
        </div>
      )}

      {events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No Events</h3>
          <p>Create your first event to get started</p>
        </div>
      ) : (
        <div className="events-list">
          {events.map(event => (
            <div key={event.id} className="event-card" style={{ cursor: 'pointer' }} onClick={() => setSelectedEvent(event)}>
              {event.imageURL ? (
                <div className="event-image">
                  <img src={event.imageURL} alt={event.name} />
                </div>
              ) : (
                <div className="event-icon">{getEventIcon(event.eventType)}</div>
              )}
              <div className="event-info">
                <h4>{event.name}</h4>
                <p>{event.eventType}</p>
                {(schoolNameFor(event.schoolId) || event.schoolId) && <p>🏫 {schoolNameFor(event.schoolId) || event.schoolId}</p>}
                <p>Date: {formatDate(event.date)}</p>
                {event.location && typeof event.location === 'object' && event.location.address && <p>📍 {event.location.address}</p>}
                {event.location && typeof event.location === 'string' && <p>📍 {event.location}</p>}
                {event.description && <p>{event.description}</p>}
              </div>
              <div className="event-status">
                <span className={`status-badge ${event.status}`}>{event.status}</span>
              </div>
              <button
                className="delete-button"
                onClick={(e) => { e.stopPropagation(); handleDeleteEvent(event.id); }}
              >
                🗑️
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default SuperAdminEventsView;
